import type { ComponentType } from 'react';
import { useEffect } from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { colors } from '../../theme/colors';
import { easings } from '../../theme/motion';
import { tokens } from '../../theme/tokens';
import { Text } from './Text';

// Solid fill matching colors.background.primary so the BottomNav reads as
// the exact same tone as the root SafeAreaView and the Header.
const NAV_FILL = colors.background.primary;
const NAV_HEIGHT = 64;

export interface BottomNavTab {
  key: string;
  label: string;
  testID?: string;
  icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  onPress: () => void;
}

interface BottomNavProps {
  tabs: BottomNavTab[];
  activeKey: string;
  style?: ViewStyle;
}

export function BottomNav({ tabs, activeKey, style }: Readonly<BottomNavProps>) {
  const insets = useSafeAreaInsets();

  return (
    <View
      style={[
        styles.shell,
        {
          paddingBottom: insets.bottom,
          height: NAV_HEIGHT + insets.bottom,
          backgroundColor: NAV_FILL,
        },
        style,
      ]}
    >
      <View accessibilityRole="tablist" style={styles.row}>
        {tabs.map((tab) => (
          <BottomNavItem key={tab.key} tab={tab} active={tab.key === activeKey} />
        ))}
      </View>
    </View>
  );
}

interface BottomNavItemProps {
  tab: BottomNavTab;
  active: boolean;
}

function BottomNavItem({ tab, active }: Readonly<BottomNavItemProps>) {
  const reduced = useReducedMotion();
  const indicator = useSharedValue(active ? 1 : 0);

  useEffect(() => {
    if (reduced) {
      indicator.value = active ? 1 : 0;
      return;
    }
    indicator.value = withTiming(active ? 1 : 0, {
      duration: tokens.duration.fast,
      easing: easings.out,
    });
  }, [active, reduced, indicator]);

  const indicatorStyle = useAnimatedStyle(() => ({
    opacity: indicator.value,
  }));

  const Icon = tab.icon;
  const iconColor = active ? colors.primary.main : colors.text.tertiary;
  const labelColor = active ? colors.primary.main : colors.text.tertiary;

  return (
    <Pressable
      testID={tab.testID}
      onPress={tab.onPress}
      accessibilityRole="tab"
      accessibilityState={{ selected: active }}
      accessibilityLabel={tab.label}
      style={({ pressed }) => [styles.item, pressed && styles.itemPressed]}
    >
      <Animated.View style={[styles.indicator, indicatorStyle]} />
      <View>
        <Icon size={22} color={iconColor} strokeWidth={active ? 2 : 1.6} />
      </View>
      <Text style={[styles.label, { color: labelColor }, active && styles.labelActive]}>
        {tab.label}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  shell: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border.subtle,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
  },
  item: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    paddingTop: tokens.spacing.sm,
    paddingBottom: tokens.spacing.xs,
    minHeight: 48,
  },
  itemPressed: {
    opacity: 0.76,
  },
  indicator: {
    position: 'absolute',
    top: 0,
    width: 32,
    height: 2,
    borderRadius: 1,
    backgroundColor: colors.primary.main,
  },
  label: {
    fontSize: 10,
    fontWeight: '500',
    letterSpacing: 0.2,
    marginTop: 2,
  },
  labelActive: {
    fontWeight: '600',
  },
});
